import { useRouter } from 'next/router'
import { useCallback } from 'react'

import { PENGELOLAAN_KINERJA_BASE_PATH } from 'app/KinerjaShared/constants/path'

/**
 * Options for configuring safe back navigation behavior.
 */
export type UseSafeBackNavigationOptions = {
  /**
   * Fallback route when history is insufficient (default: '/home').
   * Used when `window.history.length <= 2` (direct navigation/bookmark/external referrer).
   */
  fallbackRoute?: string

  /**
   * Check document referrer in addition to history length (default: true).
   * External referrers will trigger fallback navigation.
   * Set to `false` only if you have specific reasons to allow external navigation.
   */
  checkReferrer?: boolean

  /**
   * History length threshold (default: 2).
   * Pages with `history.length <= threshold` use fallback route.
   */
  historyThreshold?: number

  /**
   * Callback executed before navigation.
   * Useful for cleanup, analytics, or side effects.
   */
  onBeforeNavigate?: () => void | Promise<void>
}

/**
 * Hook for safe back navigation that prevents exiting the app unexpectedly.
 *
 * **Problem**: `router.back()` can navigate outside the app when users arrive via
 * direct links, bookmarks, or external sites.
 *
 * **Solution**: Uses `window.history.length` and `document.referrer` to detect "new activity":
 * - `history.length <= 2`: Navigate to fallback route (unsafe to go back)
 * - External referrer: Navigate to fallback route (prevents exiting app)
 * - `history.length > 2` + internal referrer: Use `router.back()` (safe internal navigation)
 *
 * @example
 * ```tsx
 * // Basic usage
 * const { handleBack } = useSafeBackNavigation({
 *   fallbackRoute: '/dashboard'
 * })
 *
 * <Button onClick={handleBack}>Back</Button>
 * ```
 *
 * @example
 * ```tsx
 * // With custom logic
 * const { handleBack } = useSafeBackNavigation({
 *   fallbackRoute: '/dashboard',
 *   onBeforeNavigate: async () => {
 *     await saveDraft()
 *   }
 * })
 * ```
 *
 * **Limitations**: Browser inconsistencies, history manipulation via `router.replace()`,
 * and shallow routing may affect accuracy.
 *
 * @param options - Configuration options
 * @returns Object with `handleBack` function and `isNewActivity` check
 */
export function useSafeBackNavigation(
  options: UseSafeBackNavigationOptions = {}
) {
  const router = useRouter()
  const {
    fallbackRoute = PENGELOLAAN_KINERJA_BASE_PATH,
    checkReferrer = true, // Default to true for safer navigation
    historyThreshold = 2,
    onBeforeNavigate,
  } = options

  /**
   * Checks if current navigation is "new activity" (unsafe to go back).
   * Returns `true` when history is insufficient or referrer is external.
   */
  const isNewActivity = useCallback((): boolean => {
    // Check history length
    const hasInsufficientHistory =
      typeof window !== 'undefined' && window.history.length <= historyThreshold

    // Optionally check referrer
    if (checkReferrer && typeof window !== 'undefined') {
      const referrer = document.referrer
      const isExternalReferrer =
        referrer && !referrer.includes(window.location.origin)

      return hasInsufficientHistory || isExternalReferrer
    }

    return hasInsufficientHistory
  }, [checkReferrer, historyThreshold])

  /**
   * Safe back navigation handler.
   * Navigates to fallback route if history is insufficient, otherwise uses `router.back()`.
   * Executes `onBeforeNavigate` callback before navigation if provided.
   */
  const handleBack = useCallback(async () => {
    // Execute custom callback before navigation
    if (onBeforeNavigate) {
      await onBeforeNavigate()
    }

    // Check if safe to go back
    if (isNewActivity()) {
      // History insufficient or external referrer - use fallback
      router.replace(fallbackRoute)
    } else {
      // Safe to go back - use browser history
      router.back()
    }
  }, [router, fallbackRoute, isNewActivity, onBeforeNavigate])

  return {
    /** Handler function for safe back navigation. Use as onClick handler. */
    handleBack,

    /** Check if current navigation is "new activity". Useful for conditional logic. */
    isNewActivity,
  }
}
